import React, { useState, useMemo, useEffect } from "react";
import { Search } from "lucide-react";
import { Input } from "components/ui/input";
import { Badge } from "components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "components/ui/accordion";
import { KnownQuestion, CustomQuestion } from "./CallScriptManager";
import ProcedureCodePrompt from "./ProcedureCodePrompt";

// Category display order
const CATEGORY_ORDER = [
  "PlanInformation",
  "Maximums",
  "ClaimsInformation",
  "BenefitsDetails",
  "CallDetails",
  "ExtraDentalInfo",
  "ExtraMedicalInfo",
];

interface KnownQuestionsPanelProps {
  questions: KnownQuestion[];
  categories: Record<string, string[]>;
  selectedQuestionIds: Set<string>;
  onToggleSelection: (questionId: string) => void;
  onAddCustomQuestionFromKnown?: (question: CustomQuestion) => void;
}

const KnownQuestionsPanel: React.FC<KnownQuestionsPanelProps> = ({
  questions,
  categories,
  selectedQuestionIds,
  onToggleSelection,
  onAddCustomQuestionFromKnown,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [showProcedureCodePrompt, setShowProcedureCodePrompt] = useState(false);
  const [pendingQuestion, setPendingQuestion] = useState<KnownQuestion | null>(null);

  const filteredQuestions = useMemo(() => {
    if (!searchQuery.trim()) {
      return questions;
    }

    const query = searchQuery.toLowerCase().trim();
    return questions.filter(
      (q) =>
        q.question_text.toLowerCase().includes(query) ||
        q.category.toLowerCase().includes(query) ||
        (q.results_field_path &&
          q.results_field_path.toLowerCase().includes(query)) ||
        (q.procedure_code &&
          q.procedure_code.toLowerCase().includes(query))
    );
  }, [questions, searchQuery]);

  // Group filtered questions by category
  const questionsByCategory = useMemo(() => {
    const grouped: Record<string, KnownQuestion[]> = {};

    filteredQuestions.forEach((question) => {
      const category = question.category;
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(question);
    });

    return grouped;
  }, [filteredQuestions]);

  // Expand all categories by default if none are expanded
  useEffect(() => {
    if (expandedCategories.length === 0 && Object.keys(questionsByCategory).length > 0) {
      setExpandedCategories(Object.keys(questionsByCategory));
    }
  }, [questionsByCategory, expandedCategories.length]);

  // Format category name for display (convert camelCase/PascalCase to readable)
  const formatCategoryName = (category: string): string => {
    return category
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  // Get category count
  const getCategoryCount = (category: string): number => {
    return questionsByCategory[category]?.length || 0;
  };

  // Get selected count for category
  const getSelectedCount = (category: string): number => {
    const categoryQuestions = questionsByCategory[category] || [];
    return categoryQuestions.filter((q) => selectedQuestionIds.has(q.id)).length;
  };

  // Handle question selection - detect placeholder procedure codes
  const handleQuestionToggle = (question: KnownQuestion) => {
    if (question.procedure_code === "XYZ" && !selectedQuestionIds.has(question.id)) {
      setPendingQuestion(question);
      setShowProcedureCodePrompt(true);
    } else {
      onToggleSelection(question.id);
    }
  };

  const handleProcedureCodeConfirm = (procedureCode: string) => {
    if (!pendingQuestion) return;

    const customizedQuestion: CustomQuestion = {
      ...pendingQuestion,
      id: crypto.randomUUID(),
      procedure_code: procedureCode,
      results_field_path: pendingQuestion.results_field_path?.replace(/XYZ/g, procedureCode) || null,
      question_text: pendingQuestion.question_text.replace(/XYZ/g, procedureCode),
      is_custom: true,
    };

    if (onAddCustomQuestionFromKnown) {
      onAddCustomQuestionFromKnown(customizedQuestion);
    }

    setShowProcedureCodePrompt(false);
    setPendingQuestion(null);
  };

  // Handle procedure code prompt cancellation
  const handleProcedureCodeCancel = () => {
    setShowProcedureCodePrompt(false);
    setPendingQuestion(null);
  };

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-gray-400" />
        </div>
        <Input
          type="text"
          placeholder="Search questions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 h-9"
        />
      </div>

      {/* Questions List */}
      <div className="max-h-[600px] overflow-y-auto">
        {Object.keys(questionsByCategory).length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 text-sm">
              {searchQuery
                ? "No questions match your search."
                : "No known questions available."}
            </p>
          </div>
        ) : (
          <Accordion
            type="multiple"
            value={expandedCategories}
            onValueChange={setExpandedCategories}
            className="w-full"
          >
            {Object.keys(questionsByCategory)
              .sort((a, b) => {
                const indexA = CATEGORY_ORDER.indexOf(a);
                const indexB = CATEGORY_ORDER.indexOf(b);
                // If category not in order list, put it at the end
                if (indexA === -1 && indexB === -1) return a.localeCompare(b);
                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                return indexA - indexB;
              })
              .map((category) => {
                const categoryQuestions = questionsByCategory[category];
                const selectedCount = getSelectedCount(category);
                const totalCount = getCategoryCount(category);

                return (
                  <AccordionItem
                    key={category}
                    value={category}
                    className="border border-gray-200 rounded-lg mb-2 px-3"
                  >
                    <AccordionTrigger className="hover:no-underline py-3">
                      <div className="flex items-center justify-between w-full pr-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-semibold text-gray-900">
                            {formatCategoryName(category)}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {totalCount}
                          </Badge>
                          {selectedCount > 0 && (
                            <Badge
                              variant="default"
                              className="text-xs bg-[#005151] text-white"
                            >
                              {selectedCount} selected
                            </Badge>
                          )}
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-2 pb-3">
                      <div className="space-y-2">
                        {categoryQuestions.map((question) => {
                          const isSelected = selectedQuestionIds.has(question.id);

                          return (
                            <div
                              key={question.id}
                              className={`flex items-start space-x-2 p-2 rounded-md transition-colors ${
                                isSelected
                                  ? "bg-[#005151]/10 border border-[#005151]/20"
                                  : "hover:bg-gray-50 border border-transparent"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleQuestionToggle(question)}
                                className="mt-1 h-4 w-4 text-[#005151] border-gray-300 rounded focus:ring-[#005151] focus:ring-2 cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                              />
                              <div className="flex-1 min-w-0">
                                <p
                                  className={`text-sm ${
                                    isSelected
                                      ? "font-medium text-gray-900"
                                      : "font-normal text-gray-700"
                                  }`}
                                >
                                  {question.question_text}
                                </p>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  {question.procedure_code && (
                                    <Badge
                                      variant="outline"
                                      className={`text-xs font-normal ${
                                        question.procedure_code === "XYZ"
                                          ? "bg-yellow-50 border-yellow-300 text-yellow-800"
                                          : ""
                                      }`}
                                    >
                                      Code: {question.procedure_code === "XYZ" ? "XYZ (requires code)" : question.procedure_code}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
          </Accordion>
        )}
      </div>

      {/* Summary */}
      {selectedQuestionIds.size > 0 && (
        <div className="pt-2 border-t border-gray-200">
          <p className="text-xs text-gray-600">
            {selectedQuestionIds.size} question
            {selectedQuestionIds.size !== 1 ? "s" : ""} selected
          </p>
        </div>
      )}

      {/* Procedure Code Prompt Dialog */}
      <ProcedureCodePrompt
        open={showProcedureCodePrompt}
        questionText={pendingQuestion?.question_text || ""}
        onConfirm={handleProcedureCodeConfirm}
        onCancel={handleProcedureCodeCancel}
      />
    </div>
  );
};

export default KnownQuestionsPanel;
